import {
  CREDIT_COSTS,
  CREDIT_USD_PRICE,
  OPERATION_DESCRIPTIONS,
  OPERATION_LABELS,
  STAKE_TIERS,
} from '@legate/shared';
import type { BillableOperation } from '@legate/shared';
import { Section } from './Section.js';

const OPERATIONS = Object.keys(CREDIT_COSTS) as BillableOperation[];

const usd = (credits: number) => `$${(credits * CREDIT_USD_PRICE).toFixed(2)}`;

const tokens = (value: number) =>
  value === 0 ? 'No stake' : `${new Intl.NumberFormat('en-US').format(value)} $LEGATE`;

export function PricingSection() {
  return (
    <Section
      id="pricing"
      label="Pricing"
      title={
        <>
          A credit is
          <br className="hidden sm:block" /> a unit of work
        </>
      }
      intro={`A credit is denominated in USD and priced at ${usd(1)}. It is spent on concrete system work, never on seats. Paying in $LEGATE applies a 25–35% discount, priced from a Uniswap V3 TWAP over a window of no less than 30 minutes.`}
    >
      <div className="w-full overflow-x-auto">
        <table className="w-full min-w-[560px] text-left border-collapse">
          <thead>
            <tr className="border-y border-hairline">
              <th className="py-3 pr-6 text-[11px] uppercase tracking-[0.2em] text-ink/50 font-medium w-[28%]">
                Operation
              </th>
              <th className="py-3 pr-6 text-[11px] uppercase tracking-[0.2em] text-ink/50 font-medium w-[14%]">
                Credits
              </th>
              <th className="py-3 text-[11px] uppercase tracking-[0.2em] text-ink/50 font-medium">
                What is billed
              </th>
            </tr>
          </thead>
          <tbody className="divide-y divide-hairline">
            {OPERATIONS.map((op) => (
              <tr key={op} className="align-top">
                <td className="py-4 pr-6 text-sm font-medium text-ink">{OPERATION_LABELS[op]}</td>
                <td className="py-4 pr-6 text-sm text-ink/70 tabular whitespace-nowrap">
                  {CREDIT_COSTS[op]}
                  <span className="ml-2 text-[11px] text-ink/40">{usd(CREDIT_COSTS[op])}</span>
                </td>
                <td className="py-4 text-sm text-ink/70 leading-relaxed">
                  {OPERATION_DESCRIPTIONS[op]}
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      <div className="mt-14 sm:mt-16">
        <div className="text-[11px] uppercase tracking-[0.2em] text-ink/50 font-medium">
          Stake tiers
        </div>
        <p className="mt-3 max-w-2xl text-sm md:text-[15px] text-ink/70 leading-relaxed">
          Staking pays no yield. It unlocks product limits only — accounts, monthly credit grant,
          queue priority and model access. Thresholds are fixed for a quarter.
        </p>

        <div className="mt-8 w-full overflow-x-auto">
          <table className="w-full min-w-[560px] text-left border-collapse">
            <thead>
              <tr className="border-y border-hairline">
                <th className="py-3 pr-6 text-[11px] uppercase tracking-[0.2em] text-ink/50 font-medium w-[18%]">
                  Tier
                </th>
                <th className="py-3 pr-6 text-[11px] uppercase tracking-[0.2em] text-ink/50 font-medium w-[24%]">
                  Stake
                </th>
                <th className="py-3 text-[11px] uppercase tracking-[0.2em] text-ink/50 font-medium">
                  What it unlocks
                </th>
              </tr>
            </thead>
            <tbody className="divide-y divide-hairline">
              {STAKE_TIERS.map((tier) => (
                <tr key={tier.key} className="align-top">
                  <td className="py-4 pr-6 text-sm font-medium text-ink">{tier.label}</td>
                  <td className="py-4 pr-6 text-sm text-ink/70 tabular whitespace-nowrap">
                    {tokens(tier.threshold)}
                  </td>
                  <td className="py-4 text-sm text-ink/70 leading-relaxed">
                    {tier.features.join(' · ')}
                    <span className="block text-[11px] text-ink/40 mt-1 tabular">
                      {new Intl.NumberFormat('en-US').format(tier.monthlyCreditGrant)} credits per
                      month
                      {tier.maxAccounts === -1
                        ? ' · unlimited accounts'
                        : ` · ${tier.maxAccounts} account${tier.maxAccounts === 1 ? '' : 's'}`}
                    </span>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>
    </Section>
  );
}

export default PricingSection;
